import { Box, LoadingOverlay, Tabs } from '@mantine/core';
import { RevisionGetResponse } from 'api/actions/revision-get/revision-get-response';
import { useApi } from 'api/api-context';
import {
  FillOutRevisionDataProvider,
  useFillOutRevisionDataProvider,
} from 'components/forms/revision/fill-out/data/FillOutRevisionDataProvider';
import {
  FillOutRevisionSaveDraftProvider,
  useFillOutRevisionSaveDraftProvider,
} from 'components/forms/revision/fill-out/data/FillOutRevisionSaveDraftProvider';
import {
  FillOutRevisionSubmitProvider,
  useFillOutRevisionSubmitProvider,
} from 'components/forms/revision/fill-out/data/FillOutRevisionSubmitProvider';
import { RevisionLockProvider } from 'components/forms/revision/fill-out/data/RevisionLockProvider';
import DevicesTabContent from 'components/forms/revision/fill-out/devices/DevicesTabContent';
import CopyPastRevisionModal from 'components/forms/revision/fill-out/modals/copy-past-revision-modal/CopyPastRevisionModal';
import { DeviceFaultProvider } from 'components/forms/revision/fill-out/modals/device-fault-modal/DeviceFaultProvider';
import { DeviceMeasurementProvider } from 'components/forms/revision/fill-out/modals/device-measurement-modal/DeviceMeasurementProvider';
import RevisionChangesModal from 'components/forms/revision/fill-out/modals/revision-changes-modal/RevisionChangesModal';
import SubmitRevisionModal from 'components/forms/revision/fill-out/modals/submit-revision-modal/SubmitRevisionModal';
import RevisionMetadata from 'components/forms/revision/fill-out/RevisionMetadata';
import RevisionLockDebugPanel from 'components/forms/revision/fill-out/RevisionLockDebugPanel';
import RevisionTab from 'components/forms/revision/fill-out/RevisionTab';
import RevisionTabContent from 'components/forms/revision/fill-out/RevisionTabContent';
import RevisionFormFooter from 'components/forms/RevisionFormFooter';
import RevisionPreview from 'components/revision/RevisionPreview';
import {
  FEATURE_TOGGLE_REVISION_CHANGES_MODAL,
  FEATURE_TOGGLE_REVISION_LOCK_DEBUG_PANEL,
  FEATURE_TOGGLE_TECHNICIAN_COPY_REVISION_DATA,
} from 'env';
import useTabSteps from 'hooks/use-tab-steps';
import { useMemo } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { FORM_PAGE_PATH, REVISION_PAGE_PATH } from 'routes/paths';

/**
 * The form for filling out a revision.
 */
function FillOutRevisionFormImpl() {
  const { userId } = useApi();
  const { saveDraft, loading: loadingSave } = useFillOutRevisionSaveDraftProvider();
  const { loading: loadingSubmit } = useFillOutRevisionSubmitProvider();
  const { revision, form, formKey, sectionsByTab, tabHasVisibleInputs } = useFillOutRevisionDataProvider();

  const [submitModalOpened, { open: openSubmitModal, close: closeSubmitModal }] = useDisclosure(false);

  const isDelegatedToMe = useMemo(
    () => revision.delegatedTo?.some((user) => user.userId === userId),
    [revision, userId]
  );

  const tabs = useMemo(
    () => [
      { key: 'basic-information', title: 'Základné informácie' },
      { key: 'description', title: 'Popis', hidden: !tabHasVisibleInputs('description') },
      { key: 'specification', title: 'Špecifikácia', hidden: !tabHasVisibleInputs('specification') },
      {
        key: 'devices',
        title: 'Zariadenia',
        hidden:
          !tabHasVisibleInputs('device') && !tabHasVisibleInputs('faults') && !tabHasVisibleInputs('measurements'),
      },
      { key: 'attachments', title: 'Prílohy', hidden: !tabHasVisibleInputs('attachments') },
      { key: 'conclusion', title: 'Záver', hidden: !tabHasVisibleInputs('conclusion') },
      { key: 'preview', title: 'Náhľad' },
    ],
    [tabHasVisibleInputs]
  );

  const { steps, currStep, setCurrStep, isFirstStep, isLastStep, nextStep, prevStep } = useTabSteps(tabs);

  return (
    <Box w="100%" className="rounded-[4px]" bg="white" pos="relative" key={formKey}>
      <LoadingOverlay visible={loadingSubmit} />
      {FEATURE_TOGGLE_REVISION_LOCK_DEBUG_PANEL && <RevisionLockDebugPanel />}
      <Tabs value={currStep.key} onTabChange={(value) => setCurrStep(value ?? steps[0].key)}>
        <Tabs.List>
          {tabs
            .filter(({ hidden }) => !hidden)
            .map(({ key, title }) => (
              <Tabs.Tab key={key} value={key}>
                {title}
              </Tabs.Tab>
            ))}
        </Tabs.List>

        <RevisionTab value={steps[0].key}>
          <RevisionMetadata />
          <RevisionTabContent sections={sectionsByTab.info} />
        </RevisionTab>
        <RevisionTab value={steps[1].key}>
          <RevisionTabContent sections={sectionsByTab.description} />
        </RevisionTab>
        <RevisionTab value={steps[2].key}>
          <RevisionTabContent sections={sectionsByTab.specification} />
        </RevisionTab>
        <RevisionTab value={steps[3].key}>
          <DevicesTabContent />
        </RevisionTab>
        <RevisionTab value={steps[4].key}>
          <RevisionTabContent sections={sectionsByTab.attachments} />
        </RevisionTab>
        <RevisionTab value={steps[5].key}>
          <RevisionTabContent sections={sectionsByTab.conclusion} />
        </RevisionTab>
        {currStep === steps[6] && (
          <RevisionTab skinny value={steps[6].key}>
            <RevisionPreview key={currStep.key} revisionData={form.values} revision={revision} />
          </RevisionTab>
        )}
      </Tabs>

      <RevisionFormFooter
        revisionKind={revision.kind === 'revision' ? 'revision' : 'form'}
        loading={loadingSave}
        isFirstStep={isFirstStep}
        isLastStep={isLastStep}
        canSubmit={!isDelegatedToMe}
        onPrevStep={prevStep}
        onNextStep={nextStep}
        onSave={saveDraft}
        onSubmit={openSubmitModal}
      />
      <SubmitRevisionModal opened={submitModalOpened} onClose={closeSubmitModal} />
    </Box>
  );
}

/**
 * The form for filling out a revision.
 */
export default function FillOutRevisionForm({
  revision,
  refetchRevision,
}: {
  revision: RevisionGetResponse;
  refetchRevision: () => Promise<RevisionGetResponse | null>;
}) {
  const dataProviderKey = useMemo(
    () => `${revision.revisionId}:${revision.revisionData ?? ''}`,
    [revision.revisionData, revision.revisionId]
  );

  const redirectPath = useMemo(
    () =>
      revision.kind === 'revision'
        ? REVISION_PAGE_PATH.insert({ revisionId: revision.revisionId })
        : FORM_PAGE_PATH.insert({ revisionId: revision.revisionId }),
    [revision.kind, revision.revisionId]
  );

  return (
    <RevisionLockProvider
      revisionId={revision.revisionId}
      revisionKind={revision.kind}
      departmentId={revision.department.departmentId}
      redirectPath={redirectPath}
      onTakeoverRefetch={refetchRevision}
    >
      <FillOutRevisionDataProvider key={dataProviderKey} revision={revision}>
        <FillOutRevisionSaveDraftProvider>
          <FillOutRevisionSubmitProvider>
            <DeviceFaultProvider>
              <DeviceMeasurementProvider>
                <FillOutRevisionFormImpl />
                {FEATURE_TOGGLE_REVISION_CHANGES_MODAL && <RevisionChangesModal />}
                {FEATURE_TOGGLE_TECHNICIAN_COPY_REVISION_DATA && <CopyPastRevisionModal />}
              </DeviceMeasurementProvider>
            </DeviceFaultProvider>
          </FillOutRevisionSubmitProvider>
        </FillOutRevisionSaveDraftProvider>
      </FillOutRevisionDataProvider>
    </RevisionLockProvider>
  );
}
